import { ArrowRight } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import { insightToolCallouts, toolBySlug, toolCalloutHref } from "@/data/tools";
import type { ToolSlug } from "@/lib/tools/calc";

/** Footer callout card for an insight article. */
export function ToolCallout({ tool, articleSlug, className = "" }: { tool: ToolSlug; articleSlug: string; className?: string }) {
  const t = toolBySlug(tool);
  return (
    <aside className={`card-panel ${className}`} aria-label={`Free tool: ${t.name}`}>
      <span className="eyebrow">Free tool</span>
      <p className="mt-2 font-serif text-2xl text-midnight">{t.name}</p>
      <p className="mt-2 text-charcoal">{t.oneLine}</p>
      <a
        href={toolCalloutHref(tool)}
        onClick={() => trackEvent("tool_callout_click", { article_slug: articleSlug, source_tool: tool })}
        className="mt-4 inline-flex min-h-11 items-center gap-2 rounded-full bg-midnight px-6 text-sm font-semibold text-frost transition hover:bg-midnight-deep"
      >
        Try the {t.name} <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </a>
    </aside>
  );
}

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] ?? c);

function calloutHtml(tool: ToolSlug, articleSlug: string) {
  const t = toolBySlug(tool);
  return `<aside class="tool-callout card-panel" aria-label="Free tool: ${esc(t.name)}"><p class="eyebrow">Free tool</p><p class="tool-callout-name">${esc(t.name)}</p><p class="tool-callout-line">${esc(t.oneLine)}</p><a class="tool-callout-btn" href="${esc(toolCalloutHref(tool))}" data-tool-callout="${tool}" data-article-slug="${esc(articleSlug)}">Try the ${esc(t.name)}</a></aside>`;
}

/** Inserts the mapped callouts at the end of the second H2 section (before the third H2). */
export function withToolCallouts(articleSlug: string, html: string): string {
  const mapped = insightToolCallouts[articleSlug];
  if (!mapped?.length) return html;
  const block = mapped.map((t) => calloutHtml(t, articleSlug)).join("");
  const re = /(?:<p class="kicker">[\s\S]*?<\/p>\s*)?<h2\b/g;
  let m: RegExpExecArray | null;
  let n = 0;
  while ((m = re.exec(html))) {
    n += 1;
    if (n === 3) return html.slice(0, m.index) + block + html.slice(m.index);
  }
  return html + block;
}

/** Delegated click handler for callouts injected into article HTML. */
export function onArticleCalloutClick(e: React.MouseEvent) {
  const a = (e.target as HTMLElement).closest?.("a[data-tool-callout]") as HTMLAnchorElement | null;
  if (!a) return;
  trackEvent("tool_callout_click", { article_slug: a.dataset.articleSlug ?? "", source_tool: a.dataset.toolCallout ?? "" });
}
